import { CustomColumnDef, defaultCustomColumnDef, TableOptions } from '@/types/table';
import { getGroupedRowModel, GroupingState, PaginationState, Row, Table } from '@tanstack/react-table';
import { Dispatch, RefObject, SetStateAction, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useTableConfig } from '../hooks/useTableConfig';
import { useTableStoreSync } from '../hooks/useTableStoreSync';
import { TableProps } from '../Table';
import {
  buildColumnOffsets,
  ColumnOffsets,
  distributeColumnSizes,
  getPositionedColumnDefs,
  PositionedColumnDef,
} from '../utils/columnLayout';
import {
  DEFAULT_MAX_COLUMN_SIZE,
  DEFAULT_MIN_COLUMN_SIZE,
  DEFAULT_PAGE_SIZE,
  DEFAULT_TABLE_WIDTH,
  ROW_EXPANSION_ANIMATION_DURATION,
  ROW_HEIGHT_IN_REM,
} from '../utils/constants';
import { makeGetCustomGroupedRowModel } from '../utils/GroupRowModelFactory';
import {
  getAdditionalHeaderLabels,
  getNumberOfRowsByGrouping,
  normalizeMergedGroups,
  sortColumnsByOrder,
} from '../utils/utils';

const useToggleRow = <T>(setRowExpandedDelayed: Dispatch<SetStateAction<Record<string, boolean>>>) =>
  useCallback(
    (row: Row<T>) => {
      if (!row.getIsExpanded()) {
        row.toggleExpanded();
        requestAnimationFrame(() => setRowExpandedDelayed(prevState => ({ ...prevState, [row.id]: true })));
      } else {
        setRowExpandedDelayed(prevState => ({ ...prevState, [row.id]: false }));
        setTimeout(() => row.toggleExpanded(), ROW_EXPANSION_ANIMATION_DURATION);
      }
    },
    [setRowExpandedDelayed],
  );

const useVisibleTotalCount = <T>(
  table: Table<T>,
  lazyConfig: TableOptions<T>['lazy'],
  totalCount: number,
  grouping: GroupingState,
): number =>
  useMemo(() => {
    if (lazyConfig) {
      return totalCount;
    }

    const filteredData = table.getFilteredRowModel().flatRows.map(filteredRow => filteredRow.original);
    return getNumberOfRowsByGrouping(filteredData, grouping);
  }, [grouping, lazyConfig, table, totalCount]);

const useContainerOverflow = (tableWrapperRef: RefObject<HTMLDivElement | null>) => {
  const [containerWidth, setContainerWidth] = useState(DEFAULT_TABLE_WIDTH);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);

  useLayoutEffect(() => {
    const el = tableWrapperRef.current;
    if (!el) return;

    let lastWidth = DEFAULT_TABLE_WIDTH;
    let prevWidth = DEFAULT_TABLE_WIDTH;
    let lastOverflow = false;

    let rafId = 0;

    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const overflow = el.scrollWidth > el.clientWidth;
        if (overflow !== lastOverflow) {
          lastOverflow = overflow;
          setHasHorizontalOverflow(overflow);
        }
        const newWidth = Math.floor(el.clientWidth);
        if (newWidth <= 0 || newWidth === lastWidth) return;

        if (newWidth === prevWidth && newWidth !== lastWidth) {
          const stableWidth = Math.min(newWidth, lastWidth);
          prevWidth = lastWidth;
          lastWidth = stableWidth;
          setContainerWidth(stableWidth);
          return;
        }

        prevWidth = lastWidth;
        lastWidth = newWidth;
        setContainerWidth(newWidth);
      });
    });

    observer.observe(el);

    const table = el.querySelector('table');
    if (table) observer.observe(table);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [tableWrapperRef]);

  return { containerWidth, hasHorizontalOverflow };
};

export interface UseTableInternalStateReturn<T> {
  table: Table<T>;
  tableWrapperRef: RefObject<HTMLDivElement | null>;
  tableHeight: string;
  containsStickyColumns: boolean;
  positionedColumnDefs: PositionedColumnDef<T>[];
  columnOffsets: ColumnOffsets;
  toggleRow: (row: Row<T>) => void;
  rowExpandedDelayed: Record<string, boolean>;
  grouping: GroupingState;
  pagination: PaginationState;
  totalCount: number;
  visibleColumnCount: number;
  orderedColumns: CustomColumnDef<T>[];
  isAdjustTableOpen: boolean;
  setIsAdjustTableOpen: Dispatch<SetStateAction<boolean>>;
  setColumnOrder: (id: string, columnIdKey: string, columnOrder: string[]) => void;
  columnIdKey: string;
  columnOrder: string[];
  additionalHeaderLabels: Record<string, string> | undefined;
  resetColumnState: (id: string, columnIdKey: string) => void;
  hasHorizontalOverflow: boolean;
}

export const useTableInternalState = <T>({
  columns,
  data,
  filter,
  options,
  id,
  isLoading,
  enableColumnResizing = false,
  externalInteractionSignal,
}: Pick<
  TableProps<T>,
  'columns' | 'data' | 'filter' | 'options' | 'isLoading' | 'enableColumnResizing' | 'externalInteractionSignal'
> & { id: string }): UseTableInternalStateReturn<T> => {
  const {
    expanded,
    setExpanded,
    rowExpandedDelayed,
    setRowExpandedDelayed,
    globalFilter,
    setGlobalFilter,
    rowSelection,
    setRowSelection,
    isAdjustTableOpen,
    setIsAdjustTableOpen,
    tableWrapperRef,
    columnIdKey,
    rowSelectionEnabled,
    pagination,
    setPagination,
    sorting,
    setSorting,
    grouping,
    setGrouping,
    rowPinning,
    setRowPinning,
    columnOrder,
    columnSizing,
    columnVisibility,
    totalCount,
    lazyConfig,
    pageSize,
    autoResetPageIndex,
    setColumnOrder,
    setColumnSizing,
    resetColumnState,
  } = useTableStoreSync({ columns, data, filter, options, id, isLoading, externalInteractionSignal });

  const { containerWidth, hasHorizontalOverflow } = useContainerOverflow(tableWrapperRef);

  const customColumns = useMemo(() => {
    const cols = columns.map(column => {
      const defaultMin = column.minSize ?? DEFAULT_MIN_COLUMN_SIZE;
      return {
        ...defaultCustomColumnDef,
        ...column,
        enableResizing: enableColumnResizing,
        minSize: Math.min(defaultMin, column.size ?? defaultMin),
        maxSize: column.maxSize ?? DEFAULT_MAX_COLUMN_SIZE,
      };
    });

    distributeColumnSizes(cols, DEFAULT_MIN_COLUMN_SIZE, containerWidth);

    cols.forEach(col => {
      if (col.size && col.minSize && col.minSize > col.size) {
        col.minSize = col.size;
      }
    });

    return normalizeMergedGroups(cols);
  }, [columns, enableColumnResizing, containerWidth]);

  const customGroupedRowModel = useMemo(() => {
    if (typeof options?.shouldFlattenGroup === 'function' || typeof options?.shouldRenderSubRow === 'function') {
      return makeGetCustomGroupedRowModel(options.shouldFlattenGroup, options.shouldRenderSubRow);
    }
    return getGroupedRowModel;
  }, [options?.shouldFlattenGroup, options?.shouldRenderSubRow]);

  const table = useTableConfig({
    id,
    data,
    customColumns,
    enableColumnResizing,
    rowSelectionEnabled,
    lazyConfig,
    options,
    totalCount,
    sorting,
    pagination,
    autoResetPageIndex,
    globalFilter,
    columnVisibility,
    columnOrder,
    columnSizing,
    expanded,
    rowSelection,
    grouping,
    rowPinning,
    columnIdKey,
    containerWidth,
    customGroupedRowModel: customGroupedRowModel(),
    setColumnOrder,
    setColumnSizing,
    setRowPinning,
    setGrouping,
    setSorting,
    setPagination,
    setGlobalFilter,
    setExpanded,
    setRowSelection,
  });

  const [firstHeaderGroup] = table.getHeaderGroups();
  const visibleHeaders = firstHeaderGroup?.headers?.filter(headerCell => headerCell.column.getIsVisible()) ?? [];
  const positionedColumnDefs = getPositionedColumnDefs(visibleHeaders, options?.additionalHeaderRows?.[0]);

  const hasRowExtension = !!options?.rowExtension;
  const columnOffsets = useMemo(
    () =>
      buildColumnOffsets(
        positionedColumnDefs,
        hasRowExtension,
        options?.verticalColumn?.numberOfColspan,
        Boolean(options?.verticalColumn),
      ),
    [positionedColumnDefs, hasRowExtension, options?.verticalColumn],
  );

  const toggleRow = useToggleRow<T>(setRowExpandedDelayed);

  const visibleRowCount = table.getRowModel().rows.length;
  const effectiveRowCount =
    visibleRowCount > 0 ? Math.min(pageSize ?? DEFAULT_PAGE_SIZE, visibleRowCount) : (pageSize ?? DEFAULT_PAGE_SIZE);
  const tableHeight = options?.scrollMode ? `${(effectiveRowCount + 1) * ROW_HEIGHT_IN_REM + 0.1}rem` : 'auto';

  const visibleTotalCount = useVisibleTotalCount(table, lazyConfig, totalCount, grouping);

  const containsStickyColumns = useMemo(() => columns.some(column => column.sticky), [columns]);
  const visibleColumnCount = columns.filter(column => column.visible === undefined || column.visible).length;
  const orderedColumns = useMemo(() => sortColumnsByOrder(customColumns, columnOrder), [customColumns, columnOrder]);

  const additionalHeaderLabels = useMemo(
    () => getAdditionalHeaderLabels(options?.additionalHeaderRows),
    [options?.additionalHeaderRows],
  );

  return {
    table,
    tableWrapperRef,
    tableHeight,
    containsStickyColumns,
    positionedColumnDefs,
    columnOffsets,
    toggleRow,
    rowExpandedDelayed,
    grouping,
    pagination,
    totalCount: visibleTotalCount,
    visibleColumnCount,
    orderedColumns,
    isAdjustTableOpen,
    setIsAdjustTableOpen,
    setColumnOrder,
    columnIdKey,
    columnOrder,
    additionalHeaderLabels,
    resetColumnState,
    hasHorizontalOverflow,
  };
};
